import { DOMPurify as DOMPurifyWithTargetBlankHook } from "component-library/src/components/Molecules/RichTextArea/utils";

/** Same pattern as FurtherInformation for auto-linking bare URLs in text nodes. */
const URLS_PATTERN =
  /(https?:\/\/)?(www\.)[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,4}\b([-a-zA-Z0-9@:%_+.~#?&/=]*)|(https?:\/\/)?(www\.)?(?!ww)[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,4}\b([-a-zA-Z0-9@:%_+.~#?&/=]*)/gi;

const HIGHLIGHTS_ALLOWED_TAGS = [
  "a",
  "b",
  "strong",
  "i",
  "em",
  "u",
  "ul",
  "ol",
  "li",
  "br",
  "p",
];

const HIGHLIGHTS_PURIFY_CONFIG = {
  ALLOWED_TAGS: HIGHLIGHTS_ALLOWED_TAGS,
  ALLOWED_ATTR: ["href"],
  ALLOWED_URI_REGEXP: /^(?:(?:https?:|mailto:))/i,
};

function purifyHighlightsHtml(html: string): string {
  return DOMPurifyWithTargetBlankHook.sanitize(html, HIGHLIGHTS_PURIFY_CONFIG);
}

function newlinesToBr(value: string): string {
  return value.replaceAll("\r\n", "\n").replaceAll("\n", "<br />");
}

/**
 * Sanitize highlights for API storage and for re-display after load.
 * Converts newlines to `<br />`, then applies DOMPurify with a strict allow-list.
 */
export function sanitizeHighlightsForStorage(value: string): string {
  return purifyHighlightsHtml(newlinesToBr(value));
}

/**
 * Stored highlights use `<br />` for line breaks (see `sanitizeHighlightsForStorage`).
 * For a plain textarea editor, convert those back to `\n` so breaks match read-only HTML rendering.
 * Other allowed tags are left unchanged.
 */
export function highlightsStorageHtmlToDraftString(html: string): string {
  return html.replaceAll("\r\n", "\n").replaceAll(/<br\s*\/?>/gi, "\n");
}

function linkifyTextNodesInElement(root: HTMLElement): void {
  const textNodes: Text[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const t = node as Text;

    if (t.parentElement?.closest("a")) {
      continue;
    }

    textNodes.push(t);
  }

  for (const textNode of textNodes) {
    const text = textNode.textContent ?? "";

    if (!text) {
      continue;
    }

    const re = new RegExp(URLS_PATTERN.source, URLS_PATTERN.flags);
    const matches = [...text.matchAll(re)];

    if (matches.length === 0) {
      continue;
    }

    if (!textNode.parentNode) {
      continue;
    }

    const fragment = document.createDocumentFragment();
    let lastIndex = 0;

    for (const m of matches) {
      const idx = m.index ?? 0;
      const raw = m[0];

      if (idx > lastIndex) {
        fragment.appendChild(
          document.createTextNode(text.slice(lastIndex, idx))
        );
      }

      const a = document.createElement("a");
      a.appendChild(document.createTextNode(raw));
      a.href = /^https?:\/\//i.test(raw) ? raw : `http://${raw}`;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      fragment.appendChild(a);
      lastIndex = idx + raw.length;
    }

    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }

    textNode.replaceWith(fragment);
  }
}

/**
 * Sanitize, linkify bare URLs in text nodes only, then sanitize again.
 * For read-only UI; uses `document` (browser / jsdom).
 */
export function sanitizeHighlightsForDisplay(value: string): string {
  const sanitizedHtmlBeforeLinkification = sanitizeHighlightsForStorage(value);
  const container = document.createElement("div");
  container.innerHTML = sanitizedHtmlBeforeLinkification;
  linkifyTextNodesInElement(container);

  return purifyHighlightsHtml(container.innerHTML);
}

/** Visible character count for expand/collapse thresholds (ignores HTML tags). */
export function highlightsPlainTextLength(html: string): number {
  const div = document.createElement("div");
  div.innerHTML = html;

  return div.textContent?.length ?? 0;
}

/**
 * Collapsed + "more" when over char budget or structural hints suggest more than
 * `structuralLineHint` visual rows (see `<br>` / blocks). Defaults match a 3-line clamp.
 */
export function highlightsExceedsShowMoreThreshold(
  html: string,
  charLimit: number,
  structuralLineHint: number = 3
): boolean {
  if (highlightsPlainTextLength(html) > charLimit) {
    return true;
  }

  if (typeof document === "undefined") {
    return false;
  }

  const div = document.createElement("div");
  div.innerHTML = html;

  if (div.querySelectorAll("br").length >= structuralLineHint) {
    return true;
  }

  if (div.querySelectorAll("p").length > structuralLineHint) {
    return true;
  }

  if (div.querySelectorAll("li").length > structuralLineHint) {
    return true;
  }

  return false;
}
